import type { Access, ServerView } from '../api/servers';
import { ca } from '../i18n/ca';

export interface ServerMod {
  /** The Workshop id; null for a mod the panel gave no numeric id for. */
  id: number | null;
  name: string;
}

/**
 * Names of mods that hold other mods up rather than being what a night is
 * about: frameworks and libraries (CBA), compatibility patches and fixes, the
 * editors' and Zeus's tools. The panel gives a server's mods a name and an id
 * and nothing else (no size, no dependency list), so the name is the only
 * signal there is.
 */
const SUPPORTING =
  /\bcba\b|cba_a3|compat|\bpatch(es)?\b|\b(hot)?fix(es)?\b|\bframework\b|\bcore\b|\blib(rary)?\b|\btools?\b|\butilit|\bzeus\b|\bzen\b|\b3den\b|\beden\b|\bachilles\b|\bserver\s?mod\b/i;

/**
 * How many places a mosaic has: three rows of eight on a card, three of sixteen
 * on the server's page (the owner, test card 169, 2026-10-17: "more icons"; it
 * was two rows of six and of twelve).
 */
export const MOSAIC_CAPACITY = { card: 24, wide: 48 } as const;

/**
 * The mods a server's mosaic shows: the server's own order with the supporting
 * mods (see `SUPPORTING`) after the rest, so the first tiles are what a night
 * is about. When there are more than `capacity`, the last place is the count
 * of the others (`more`) and `capacity - 1` tiles are shown. The same server
 * always gets the same tiles.
 */
export function mosaicMods(
  mods: ServerMod[],
  capacity: number,
): { tiles: ServerMod[]; more: number } {
  const ordered = [
    ...mods.filter((m) => !SUPPORTING.test(m.name)),
    ...mods.filter((m) => SUPPORTING.test(m.name)),
  ];
  if (ordered.length <= capacity) return { tiles: ordered, more: 0 };
  const shown = Math.max(capacity - 1, 0);
  return { tiles: ordered.slice(0, shown), more: ordered.length - shown };
}

/** Two letters for a mod with no picture: the first of its first two words, or its first two. */
export function modInitials(name: string): string {
  const words = name.split(/[^\p{L}\p{N}]+/u).filter(Boolean);
  if (words.length === 0) return '?';
  const letters = words.length === 1 ? words[0].slice(0, 2) : words[0][0] + words[1][0];
  return letters.toUpperCase();
}

/**
 * The terrain a server is playing on: the world its mission file names
 * (`name.world`), or what the running game reports. Null when neither says, and
 * always null for a stopped server: the panel's `missionTemplate` is the file
 * the server is configured to start with, not a mission that is on.
 */
export function serverWorld(server: Pick<ServerView, 'missionTemplate' | 'status'>): string | null {
  if (!server.status.alive) return null;
  const template = server.missionTemplate;
  if (template && template.includes('.')) return template.slice(template.lastIndexOf('.') + 1);
  return server.status.map || null;
}

/**
 * The mission a running server has on, by its title; null for a stopped one.
 * The panel's server record keeps `missionTemplate` (the folder the server will
 * start with) whether or not the server runs, so a stopped server showed a
 * mission that was not on (the owner, test card 126, 2026-10-13).
 */
export function missionOn(
  server: Pick<ServerView, 'missionTitle' | 'missionTemplate' | 'status'>,
): string | null {
  if (!server.status.alive) return null;
  return server.missionTitle ?? server.missionTemplate ?? null;
}

/** "En marxa · 24 min" or "Aturat": the text of a server's state tag. */
export function stateWord(alive: boolean, uptime: string | null): string {
  return alive ? ca.serversPage.stateRunning(uptime) : ca.serversPage.stateStopped;
}

/** Whose server it is, in one label, with what the platform may not do as a second, quieter one. */
export function originOf(access: Access): { label: string; note: string | null } {
  const o = ca.serversPage.origin;
  return access === 'MANAGED'
    ? { label: o.platform, note: null }
    : { label: o.panel, note: access === 'NONE' ? o.readOnly : null };
}

/** Running servers first; otherwise the panel's own order. */
export function runningFirst<T extends Pick<ServerView, 'status'>>(servers: T[]): T[] {
  return servers
    .map((server, index) => ({ server, index }))
    .sort(
      (a, b) => Number(b.server.status.alive) - Number(a.server.status.alive) || a.index - b.index,
    )
    .map((entry) => entry.server);
}
